import useTableState from "@/components/Table/hooks/useTableState";
import Table from "@/components/Table/Table";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { ProductBy, useProductImpactCategories } from "@/pages/dashboard/hooks/useProductImpact";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import { useEffect } from "react";
import { FacilityImpactDetailMode } from "./CategoryImpactSideBySide";
import useImpactMetric from "@/pages/dashboard/hooks/useImpactMetric";
import { IMPACT_METRICS } from "@/pages/dashboard/utils/types";

export default function CategoryImpactPerCategory({
  mode,
  by,
}: {
  mode: FacilityImpactDetailMode;
  by: ProductBy;
}) {
  const metric = useImpactMetric();
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({ defaultPagination: { page: 1, pageSize: 10 } });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const ImpactFormatter = (value: number, total?: number | null) => {
    if (mode === "Absolute") {
      return IMPACT_METRICS.carbon_footprint.formatter(value);
    } else {
      return total ? formatPercentage(value / total) : NaN;
    }
  };

  const { data, isLoading, isFetching } = useProductImpactCategories({
    by,
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  return (
    <Table
      filter={filter}
      hideRaster={true}
      type={"grey"}
      sort={sort}
      search={search}
      columns={[
        {
          label: "Category",
          key: "category",
          sortable: true,
          renderer: (record) => record.category || "No category",
        },
        {
          label: "Scope 1",
          key: "carbon_footprint_scope_1",
          sortable: true,
          renderer: (record) =>
            ImpactFormatter(
              record.impact.product.carbon_footprint_scope_1,
              data?.total_impact.product.carbon_footprint_scope_1
            ),
        },
        {
          label: "Scope 2",
          key: "carbon_footprint_scope_2",
          sortable: true,
          renderer: (record) =>
            ImpactFormatter(
              record.impact.product.carbon_footprint_scope_2,
              data?.total_impact.product.carbon_footprint_scope_2
            ),
        },
        {
          label: "Scope 3",
          key: "carbon_footprint_scope_3",
          sortable: true,
          renderer: (record) =>
            ImpactFormatter(
              record.impact.product.carbon_footprint_scope_3,
              data?.total_impact.product.carbon_footprint_scope_3
            ),
        },
        {
          label: "Carbon footprint",
          key: "carbon_footprint",
          sortable: true,
          renderer: (record) =>
            ImpactFormatter(
              record.impact.product.carbon_footprint,
              data?.total_impact.product.carbon_footprint
            ),
        },
      ]}
      data={data?.data}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      rowKey={"key"}
    />
  );
}
